import React, { useState, useEffect, useContext } from "react";
import { LoadingButton } from "@mui/lab";
import SaveIcon from "@mui/icons-material/Save";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Layout from "../../layout/Layout";
import { clinicianDetails, putClinicianDetails } from "../../services/apiCalls";
import PhoneNumberInput from "../../components/Authentication/PhoneNumberInput.jsx";
import ErrorModal from "../../components/ErrorModal/ErrorModal.jsx";
import { DetailsToMenu } from "../../components/Menu/DetailsToMenu.jsx";

const ERROR = {
  EMAIL_REQUIRED: "emailRequired",
  EMAIL_INVALID: "emailInvalid",
  TEL_INVALID: "telephoneInvalid",
  PW_REQUIRED: "passwordRequired",
  PW_CONFIRM_REQUIRED: "passwordConfirmRequired",
  PW_STRENGTH: "passwordStrength",
  PW_SAME_AS_CURRENT: "passwordNewSameAsCurrent",
  PW_MISMATCH: "passwordsMismatch",
};
const REQUIRED_FIELDS = [
  "full_name",
  "email",
  "location",
  "organization",
  "specilization",
];

export default function Profile() {
  const { setClinicianName } = useContext(DetailsToMenu);
  const { t } = useTranslation("profile");
  const { t: tErr } = useTranslation("errors");

  const [currentPassword, setCurrentPassword] = useState("");
  const [isDetailsEditing, setIsDetailsEditing] = useState(false);
  const [isPasswordEditing, setIsPasswordEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [savingDetails, setSavingDetails] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (e) => e.preventDefault();

  const [profile, setProfile] = useState({
    full_name: "",
    email: "",
    location: "",
    organization: "",
    specilization: "",
    telephone: "",
  });
  const [initialProfile, setInitialProfile] = useState({
    full_name: "",
    email: "",
    location: "",
    organization: "",
    specilization: "",
    telephone: "",
  });

  const [passwords, setPasswords] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [selectedCountry, setSelectedCountry] = useState("gr");

  const [errorOpen, setErrorOpen] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    const getUserProfile = async () => {
      const userProfile = await clinicianDetails();
      const next = {
        full_name: userProfile?.full_name || "",
        email: userProfile?.email || "",
        location: userProfile?.location || "",
        organization: userProfile?.organization || "",
        specilization: userProfile?.specilization || "",
        telephone: userProfile?.telephone || "",
      };
      setProfile(next);
      setInitialProfile(next);
      // console.log("Initial profile: ", next);
      setCurrentPassword(userProfile?.password);
    };
    getUserProfile();
  }, []);

  const handleProfileChange = (e) => {
    setProfile((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const passwordValidator = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/;
  const emailValidator = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

  const isBlank = (v) => !String(v ?? "").trim();
  const missingRequired = REQUIRED_FIELDS.filter((k) => isBlank(profile[k]));
  const hasMissingRequired = isDetailsEditing && missingRequired.length > 0;

  const normalize = (obj) =>
    Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [
        k,
        typeof v === "string" ? v.trim() : v,
      ])
    );

  const profilesComparison =
    JSON.stringify(normalize(profile)) !==
    JSON.stringify(normalize(initialProfile));

  const hasDetailsErrors = !!(errors.email || errors.telephone);
  const fieldError = (key) =>
    isDetailsEditing && REQUIRED_FIELDS.includes(key) && isBlank(profile[key]);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;

    const next = { ...passwords, [name]: value };
    setPasswords(next);

    const newErrors = { ...errors };

    if (name === "newPassword") {
      if (!passwordValidator.test(value)) {
        newErrors.newPassword = ERROR.PW_STRENGTH;
      } else if (value === currentPassword) {
        newErrors.newPassword = ERROR.PW_SAME_AS_CURRENT;
      } else {
        delete newErrors.newPassword;
      }
    }

    if (name === "confirmPassword") {
      if (!value) {
        newErrors.confirmPassword = ERROR.PW_CONFIRM_REQUIRED;
      } else if (value !== next.newPassword) {
        newErrors.confirmPassword = ERROR.PW_MISMATCH;
      } else {
        delete newErrors.confirmPassword;
      }
    }

    setErrors(newErrors);
  };

  const handlePhoneChange = (value, countryData) => {
    setProfile((prev) => ({ ...prev, telephone: value }));
    setSelectedCountry(countryData.countryCode.toUpperCase());
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    const newErrors = { ...errors };

    if (!newEmail) {
      newErrors.email = ERROR.EMAIL_REQUIRED;
    } else if (!emailValidator.test(newEmail)) {
      newErrors.email = ERROR.EMAIL_INVALID;
    } else {
      delete newErrors.email;
    }

    setErrors(newErrors);
    setProfile((prev) => ({ ...prev, email: newEmail }));
  };

  const toggleDetailsEdit = () => {
    if (isDetailsEditing) {
      setProfile(initialProfile);
      setErrors((prev) => {
        const { email, telephone, ...rest } = prev;
        return rest;
      });
    }
    setIsDetailsEditing((v) => !v);
  };

  const handleDetailsSave = async () => {
    if (errors.telephone || errors.email) return;
    setSavingDetails(true);
    try {
      const payload = { ...profile };
      if (!payload.telephone?.trim()) delete payload.telephone;
      await putClinicianDetails(profile);
      setInitialProfile(profile);
      setIsDetailsEditing(false);
      const { telephone, ...rest } = errors;
      setErrors(rest);
      setClinicianName(profile.full_name);
    } catch (error) {
      console.error("Error updating details:", error);
      setSavingDetails(false);
      setErrorTitle(tErr("errorsProfile.title"));

      setErrorMsg(tErr("errorsProfile.updateDetails"));
      setErrorOpen(true);
    } finally {
      setSavingDetails(false);
    }
  };

  const togglePasswordEdit = () => {
    if (isPasswordEditing) {
      setPasswords({ newPassword: "", confirmPassword: "" });
      setErrors((prev) => {
        const { newPassword, confirmPassword, ...rest } = prev;
        return rest;
      });
    }
    setIsPasswordEditing((v) => !v);
    setShowPassword(false);
  };

  const handlePasswordSave = async () => {
    const newErrors = {};

    if (!passwords.newPassword) {
      newErrors.newPassword = ERROR.PW_REQUIRED;
    } else if (!passwordValidator.test(passwords.newPassword)) {
      newErrors.newPassword = ERROR.PW_STRENGTH;
    } else if (passwords.newPassword === currentPassword) {
      newErrors.newPassword = ERROR.PW_SAME_AS_CURRENT;
    }

    if (!passwords.confirmPassword) {
      newErrors.confirmPassword = ERROR.PW_CONFIRM_REQUIRED;
    } else if (passwords.newPassword !== passwords.confirmPassword) {
      newErrors.confirmPassword = ERROR.PW_MISMATCH;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setShowPassword(false);
    setSavingPassword(true);
    try {
      await putClinicianDetails({ password: passwords.newPassword });
      setPasswords({ newPassword: "", confirmPassword: "" });
      setIsPasswordEditing(false);
    } catch (error) {
      setSavingPassword(false);
      console.error("Error updating password:", error);
      setErrorTitle(tErr("errorsProfile.title"));
      setErrorMsg(tErr("errorsProfile.updatePassword"));
      setErrorOpen(true);
    } finally {
      setSavingPassword(false);
    }
  };

  const handlePhoneBlur = (msg) => {
    setErrors((prev) => {
      const next = { ...prev };
      if (!msg) {
        delete next.telephone;
        return next;
      }
      const lower = String(msg).toLowerCase();
      if (lower.includes("cannot be empty")) {
        next.telephone = "telephoneRequired";
      } else {
        next.telephone = "telephoneInvalidForRegion";
      }
      return next;
    });
  };

  return (
    <Layout>
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography component="h2" variant="h5" sx={{ marginTop: "50px" }}>
            {t("profileTitle")}
          </Typography>

          <Typography
            variant="h6"
            sx={{ mb: 2, alignSelf: "flex-start", pt: 5 }}
          >
            {t("changeDetails")}
          </Typography>

          <TextField
            label={t("labels.fullName")}
            name="full_name"
            value={profile.full_name}
            onChange={handleProfileChange}
            fullWidth
            margin="normal"
            required
            error={fieldError("full_name")}
            helperText={fieldError("full_name") ? t("errors.required") : ""}
            InputProps={{ disabled: !isDetailsEditing }}
          />

          <TextField
            label={t("labels.email")}
            name="email"
            value={profile.email}
            onChange={handleEmailChange}
            fullWidth
            margin="normal"
            required
            error={!!errors.email}
            helperText={errors.email ? t(`errors.${errors.email}`) : ""}
            InputProps={{ disabled: !isDetailsEditing }}
          />

          <TextField
            label={t("labels.location")}
            name="location"
            value={profile.location}
            onChange={handleProfileChange}
            fullWidth
            margin="normal"
            required
            error={fieldError("location")}
            helperText={fieldError("location") ? t("errors.required") : ""}
            InputProps={{ disabled: !isDetailsEditing }}
          />

          <TextField
            label={t("labels.organization")}
            name="organization"
            value={profile.organization}
            onChange={handleProfileChange}
            fullWidth
            margin="normal"
            required
            error={fieldError("organization")}
            helperText={fieldError("organization") ? t("errors.required") : ""}
            InputProps={{ disabled: !isDetailsEditing }}
          />

          <TextField
            label={t("labels.specialization")}
            name="specilization"
            value={profile.specilization}
            onChange={handleProfileChange}
            fullWidth
            margin="normal"
            required
            error={fieldError("specilization")}
            helperText={fieldError("specilization") ? t("errors.required") : ""}
            InputProps={{ disabled: !isDetailsEditing }}
          />

          <PhoneNumberInput
            value={profile.telephone}
            onChange={handlePhoneChange}
            onBlur={handlePhoneBlur}
            error={
              errors.telephone
                ? t(`errors.${errors.telephone}`, {
                    country: selectedCountry.toUpperCase(),
                  })
                : ""
            }
            disabled={!isDetailsEditing}
            country={selectedCountry.toUpperCase()}
            required={false}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 2,
              width: "100%",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={toggleDetailsEdit}
            >
              {isDetailsEditing ? t("actions.cancel") : t("actions.edit")}
            </Button>
            {isDetailsEditing && (
              <LoadingButton
                variant="contained"
                color="secondary"
                loading={savingDetails}
                loadingPosition="start"
                startIcon={<SaveIcon />}
                onClick={handleDetailsSave}
                disabled={
                  !profilesComparison || hasDetailsErrors || hasMissingRequired
                }
                sx={{ "&:hover": { bgcolor: "#a15c1fff" } }}
              >
                {t("actions.save")}
              </LoadingButton>
            )}
          </Box>

          <Typography
            variant="h6"
            sx={{ mb: 2, alignSelf: "flex-start", pt: 5 }}
          >
            {t("changePassword")}
          </Typography>

          <TextField
            label={t("labels.newPassword")}
            name="newPassword"
            type={showPassword ? "text" : "password"}
            value={passwords.newPassword}
            onChange={handlePasswordChange}
            fullWidth
            margin="normal"
            required
            error={!!errors.newPassword}
            helperText={
              errors.newPassword ? t(`errors.${errors.newPassword}`) : ""
            }
            InputProps={{
              disabled: !isPasswordEditing,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {isPasswordEditing && (
            <TextField
              label={t("labels.confirmPassword")}
              name="confirmPassword"
              type={showPassword ? "text" : "password"}
              required
              value={passwords.confirmPassword}
              onChange={handlePasswordChange}
              fullWidth
              margin="normal"
              error={!!errors.confirmPassword}
              helperText={
                errors.confirmPassword
                  ? t(`errors.${errors.confirmPassword}`)
                  : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 2,
              width: "100%",
              pb: 5,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={togglePasswordEdit}
            >
              {isPasswordEditing ? t("actions.cancel") : t("actions.edit")}
            </Button>
            {isPasswordEditing && (
              <LoadingButton
                variant="contained"
                color="secondary"
                loading={savingPassword}
                disabled={
                  errors.newPassword ||
                  errors.confirmPassword ||
                  passwords.newPassword === "" ||
                  passwords.confirmPassword === ""
                }
                loadingPosition="start"
                startIcon={<SaveIcon />}
                onClick={handlePasswordSave}
                sx={{ "&:hover": { bgcolor: "#a15c1fff" }, color: "white" }}
              >
                {t("actions.save")}
              </LoadingButton>
            )}
          </Box>

          <ErrorModal
            open={errorOpen}
            onClose={() => setErrorOpen(false)}
            title={errorTitle}
            message={errorMsg}
          />
        </Box>
      </Container>
    </Layout>
  );
}
